import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Ban, Loader2, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { formatDistanceToNow } from "date-fns";

interface EntrantsListProps {
    contestId: string;
    organizationId: string;
    planKey: string;
}

interface Entrant {
    participant_id: string;
    email: string | null;
    entries_count: number;
    last_entry_at: string | null;
    ip_hash: string | null;
    device_hash: string | null;
    email_hash: string | null;
    blocked_by?: {
        id: string;
        reason: string;
        scope: "contest" | "org";
        expires_at: string | null;
    };
    created_at: string; // added for display
}

interface EntrantsData {
    entrants: Entrant[];
    total: number;
}

async function sha256(message: string) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export function EntrantsList({ contestId, organizationId, planKey }: EntrantsListProps) {
    const [participantPage, setParticipantPage] = useState(1);
    const PARTICIPANTS_PER_PAGE = 15;
    const offset = (participantPage - 1) * PARTICIPANTS_PER_PAGE;
    const [selectedEntrant, setSelectedEntrant] = useState<Entrant | null>(null);

    useEffect(() => {
        setParticipantPage(1);
    }, [contestId]);
    const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);

    // Block Form State
    const [blockReason, setBlockReason] = useState("");
    const [blockScope, setBlockScope] = useState<"contest" | "org">("contest");
    const [blockIp, setBlockIp] = useState(false);
    const [blockDevice, setBlockDevice] = useState(false);
    const [isBlocking, setIsBlocking] = useState(false);

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data: incompleteAttempts = [] } = useQuery({
        queryKey: ["entry-attempts", contestId],
        queryFn: async () => {
            const { data, error: attemptsError } = await supabase
                .from("entry_attempts")
                .select("id, email, payload, error, created_at")
                .eq("contest_id", contestId)
                .order("created_at", { ascending: false })
                .limit(50);

            if (attemptsError) {
                if (/entry_attempts|schema cache|does not exist/i.test(attemptsError.message || "")) return [];
                console.error("Failed to load incomplete submissions", attemptsError);
                return [];
            }
            return data || [];
        },
    });

    // Fetch Participants AND Blocks
    const { data: entrantsData, isLoading, isFetching, refetch } = useQuery<EntrantsData>({
        queryKey: ["admin-participants-list", contestId, participantPage],
        queryFn: async () => {

            // 1. Fetch Blocks for this Org
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data: blocks, error: blocksError } = await (supabase as any)
                .from("abuse_blocks")
                .select("*")
                .eq("organization_id", organizationId);

            if (blocksError) {
                console.error("Failed to load blocks", blocksError);
            }

            // Filter active blocks
            const activeBlocks = (blocks || []).filter((b: any) => {
                if (b.expires_at && new Date(b.expires_at) < new Date()) return false;
                if (b.scope === 'contest' && b.contest_id !== contestId) return false;
                return true;
            });

            // 2. Fetch participants slice
            // Removed email_hash from select to fix 42703 error
            const { data: participants, error: participantsError, count } = await supabase
                .from("participants")
                .select("id, email, created_at, ip_hash, device_hash", { count: 'exact' })
                .eq("contest_id", contestId)
                .order("created_at", { ascending: false })
                .range(offset, offset + PARTICIPANTS_PER_PAGE - 1);

            if (participantsError) {
                toast({ title: "Failed to load participants", description: participantsError.message, variant: "destructive" });
                throw participantsError;
            }

            const processed = await processParticipants(participants || [], activeBlocks);
            return { entrants: processed, total: count || 0 };
        },
        placeholderData: (previousData: any) => previousData, // keep previous data while fetching next page
    });

    async function processParticipants(participants: any[], blocks: any[]): Promise<Entrant[]> {
        if (!participants || participants.length === 0) return [];

        const entrantsMap = new Map<string, Entrant>();
        const participantIds: string[] = [];

        for (const p of participants) {
            // Compute email hash client-side
            let emailHash = null;
            if (p.email) {
                const normalizedEmail = p.email.trim().toLowerCase();
                emailHash = await sha256(normalizedEmail);
            }

            // Check Block Status
            const blockingRule = blocks.find(b => {
                if (b.identifier_type === 'participant_id' && b.value === p.id) return true;
                if (b.identifier_type === 'email_hash' && emailHash && b.value === emailHash) return true;
                if (b.identifier_type === 'ip_hash' && b.value === p.ip_hash) return true;
                if (b.identifier_type === 'device_hash' && b.value === p.device_hash) return true;
                return false;
            });

            entrantsMap.set(p.id, {
                participant_id: p.id,
                email: p.email,
                entries_count: 0,
                last_entry_at: null,
                ip_hash: p.ip_hash,
                device_hash: p.device_hash,
                email_hash: emailHash,
                blocked_by: blockingRule ? {
                    id: blockingRule.id,
                    reason: blockingRule.reason,
                    scope: blockingRule.scope,
                    expires_at: blockingRule.expires_at
                } : undefined,
                created_at: p.created_at
            });
            participantIds.push(p.id);
        }

        // 3. Fetch entries for these participants to aggregate counts
        if (participantIds.length > 0) {
            const { data: entries, error: entriesError } = await supabase
                .from("entries")
                .select("participant_id, created_at")
                .eq("contest_id", contestId)
                .in("participant_id", participantIds);

            if (entriesError) {
                console.error("Failed to load entrant stats", entriesError);
                toast({ title: "Failed to load entry stats", description: "Entry counts may be incomplete." });
            } else if (entries) {
                entries.forEach((entry: any) => {
                    const entrant = entrantsMap.get(entry.participant_id);
                    if (entrant) {
                        entrant.entries_count += 1;
                        const entryDate = new Date(entry.created_at);
                        if (!entrant.last_entry_at || entryDate > new Date(entrant.last_entry_at)) {
                            entrant.last_entry_at = entry.created_at;
                        }
                    }
                });
            }
        }

        return Array.from(entrantsMap.values());
    }

    function summarizeAttempt(payload: unknown): string {
        if (!payload || typeof payload !== "object" || Array.isArray(payload)) return "No fields captured";
        const record = payload as Record<string, unknown>;
        const parts: string[] = [];
        const name = [record.first_name, record.last_name].filter(Boolean).join(" ") || record.name;
        if (name) parts.push(String(name));
        if (record.phone) parts.push(String(record.phone));
        const custom = record.custom;
        if (custom && typeof custom === "object" && !Array.isArray(custom)) {
            for (const value of Object.values(custom as Record<string, unknown>)) {
                if (value !== undefined && value !== null && String(value).trim()) parts.push(String(value));
            }
        }
        return parts.join(" · ") || "No fields captured";
    }

    const handleUnblock = async (blockId: string, e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();

        if (planKey === 'free') {
            toast({
                title: "Upgrade Required",
                description: "Unblocking requires a paid plan.",
                action: (
                    <ToastAction altText="Upgrade" onClick={() => window.location.assign("/admin/settings?tab=billing")}>
                        Upgrade
                    </ToastAction>
                ),
            });
            return;
        }

        try {
            const { data: { session } } = await supabase.auth.getSession();
            const { error } = await supabase.functions.invoke('admin-abuse-unblock', {
                body: { abuse_block_id: blockId },
                headers: { Authorization: `Bearer ${session?.access_token}` }
            });

            if (error) throw error;

            toast({ title: "Unblocked successfully" });
            refetch(); // Refresh list to update status
            void queryClient.invalidateQueries({ queryKey: ["admin-abuse-blocks"] }); // Invalidate global blocks cache too
        } catch (err: any) {
            const body = (err as any).context?.body || (err as any).body;
            const code = body?.code;
            const msg = body?.error || err.message || "Failed to unblock";

            if (code === "UNAUTHENTICATED") {
                toast({ title: "Session Expired", description: "Please refresh the page and sign in again.", variant: "destructive" });
            } else if (code === "PLAN_REQUIRED") {
                toast({ title: "Feature Locked", description: "This feature requires a Starter or Growth plan.", variant: "destructive" });
            } else if (code === "FORBIDDEN") {
                toast({ title: "Access Denied", description: msg, variant: "destructive" });
            } else {
                toast({ title: "Unblock Failed", description: msg, variant: "destructive" });
            }
        }
    };

    const handleBlockClick = (entrant: Entrant, e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();

        if (planKey === 'free') {
            toast({
                title: "Upgrade Required",
                description: "Abuse protection is available on Starter and Growth plans.",
                action: (
                    <ToastAction altText="Upgrade" onClick={() => window.location.assign("/admin/settings?tab=billing")}>
                        Upgrade
                    </ToastAction>
                ),
            });
            return;
        }

        setSelectedEntrant(entrant);
        setBlockReason("Violated contest rules");
        setBlockScope("contest");
        setBlockIp(false);
        setBlockDevice(false);
        setIsBlockModalOpen(true);
    };

    const performBlock = async () => {
        if (!selectedEntrant) return;
        setIsBlocking(true);

        try {
            // Block Participant ID
            await callBlockFunction({
                contest_id: contestId,
                scope: blockScope,
                identifier_type: 'participant_id',
                value: selectedEntrant.participant_id,
                reason: blockReason
            });

            // Block Email
            if (selectedEntrant.email) {
                const normalizedEmail = selectedEntrant.email.trim().toLowerCase();
                const emailHash = await sha256(normalizedEmail);
                await callBlockFunction({
                    contest_id: contestId,
                    scope: blockScope,
                    identifier_type: 'email_hash',
                    value: emailHash,
                    reason: blockReason
                });
            }

            if (blockIp && selectedEntrant.ip_hash) {
                await callBlockFunction({
                    contest_id: contestId,
                    scope: blockScope,
                    identifier_type: 'ip_hash',
                    value: selectedEntrant.ip_hash,
                    reason: blockReason
                });
            }

            if (blockDevice && selectedEntrant.device_hash) {
                await callBlockFunction({
                    contest_id: contestId,
                    scope: blockScope,
                    identifier_type: 'device_hash',
                    value: selectedEntrant.device_hash,
                    reason: blockReason
                });
            }

            toast({ title: "Participant Blocked" });
            setIsBlockModalOpen(false);
            refetch();
            void queryClient.invalidateQueries({ queryKey: ["admin-abuse-blocks"] }); // Invalidate global blocks cache too
        } catch (e: any) {
            const body = (e as any).context?.body || (e as any).body;
            const code = body?.code;
            const msg = body?.error || e.message || "Failed to block";

            if (code === "UNAUTHENTICATED") {
                toast({ title: "Session Expired", description: "Please refresh the page and sign in again.", variant: "destructive" });
            } else if (code === "PLAN_REQUIRED") {
                toast({ title: "Feature Locked", description: "This feature requires a Starter or Growth plan.", variant: "destructive" });
            } else if (code === "FORBIDDEN") {
                toast({ title: "Access Denied", description: msg, variant: "destructive" });
            } else {
                toast({ title: "Block Failed", description: msg, variant: "destructive" });
            }
        } finally {
            setIsBlocking(false);
        }
    };

    async function callBlockFunction(body: any) {
        const { data: { session } } = await supabase.auth.getSession();
        const { error } = await supabase.functions.invoke('admin-abuse-block', {
            body,
            headers: { Authorization: `Bearer ${session?.access_token}` }
        });
        if (error) throw error;
    }

    // We use isFetching to show background opacity loading, so we can remove the full return loading here
    // or keep it only if there are NO entrants yet AND it's strictly isLoading
    if (isLoading && (!(entrantsData as EntrantsData | undefined) || (entrantsData as EntrantsData | undefined)?.total === 0)) {
        return <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;
    }

    const entrants = (entrantsData as EntrantsData | undefined)?.entrants || [];
    const totalParticipants = (entrantsData as EntrantsData | undefined)?.total || 0;
    const totalPages = Math.ceil(totalParticipants / PARTICIPANTS_PER_PAGE);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold">Participants</h2>
                {totalParticipants > 0 && <span className="text-sm text-muted-foreground">{totalParticipants} total</span>}
            </div>

            {(!entrants || entrants.length === 0) ? (
                <p className="text-muted-foreground">No participants yet.</p>
            ) : (
                <div className="space-y-4">
                    <div className={`rounded-md border ${isFetching ? "opacity-50 pointer-events-none transition-opacity duration-200" : "transition-opacity duration-200"}`}>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Entries</TableHead>
                                    <TableHead>Last Entry</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {entrants.map((entrant) => (
                                    <TableRow key={entrant.participant_id} className={entrant.blocked_by ? "bg-destructive/5" : ""}>
                                        <TableCell className="font-mono text-sm max-w-[200px] truncate" title={entrant.email || ""}>
                                            {entrant.email || <span className="text-muted-foreground italic">Anonymous</span>}
                                        </TableCell>
                                        <TableCell>
                                            {entrant.blocked_by ? (
                                                <div className="inline-flex items-center rounded-full border border-destructive bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive" title={`${entrant.blocked_by.reason} (${entrant.blocked_by.scope})`}>
                                                    Blocked
                                                </div>
                                            ) : (
                                                <div className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold text-foreground">
                                                    Active
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>{entrant.entries_count}</TableCell>
                                        <TableCell className="text-muted-foreground text-sm">
                                            {entrant.last_entry_at ? formatDistanceToNow(new Date(entrant.last_entry_at), { addSuffix: true }) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {entrant.blocked_by ? (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => handleUnblock(entrant.blocked_by!.id, e)}
                                                    className="text-muted-foreground hover:text-foreground"
                                                >
                                                    Unblock
                                                </Button>
                                            ) : (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => handleBlockClick(entrant, e)}
                                                    className={planKey === 'free' ? "text-muted-foreground opacity-50" : "text-destructive hover:text-destructive hover:bg-destructive/10"}
                                                >
                                                    {planKey === 'free' ? <Lock className="w-4 h-4 mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
                                                    Block
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                            <p className="text-sm text-muted-foreground">
                                Showing {offset + 1}–
                                {Math.min(
                                    offset + PARTICIPANTS_PER_PAGE,
                                    totalParticipants
                                )} of {totalParticipants}
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setParticipantPage(
                                        p => Math.max(1, p - 1)
                                    )}
                                    disabled={participantPage === 1 || isFetching}
                                    className="px-3 py-1 text-sm rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                                >
                                    Previous
                                </button>
                                <span className="text-sm text-muted-foreground">
                                    {participantPage} / {totalPages}
                                </span>
                                <button
                                    onClick={() => setParticipantPage(
                                        p => Math.min(totalPages, p + 1)
                                    )}
                                    disabled={
                                        participantPage === totalPages || isFetching
                                    }
                                    className="px-3 py-1 text-sm rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {incompleteAttempts.length > 0 && (
                <div className="space-y-2 pt-4 border-t border-border">
                    <h3 className="font-heading text-base font-bold">Incomplete submissions</h3>
                    <p className="text-sm text-muted-foreground">
                        These forms were filled in, but the entry did not go through. The answers are saved here.
                    </p>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Details</TableHead>
                                    <TableHead>What happened</TableHead>
                                    <TableHead>When</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {incompleteAttempts.map((attempt) => (
                                    <TableRow key={attempt.id}>
                                        <TableCell className="font-mono text-sm max-w-[180px] truncate" title={attempt.email || ""}>
                                            {attempt.email || <span className="text-muted-foreground italic">No email</span>}
                                        </TableCell>
                                        <TableCell className="text-sm max-w-[280px]">{summarizeAttempt(attempt.payload)}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground max-w-[220px]">{attempt.error || "Submit failed"}</TableCell>
                                        <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                                            {formatDistanceToNow(new Date(attempt.created_at), { addSuffix: true })}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}

            <Dialog open={isBlockModalOpen} onOpenChange={setIsBlockModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Block Participant</DialogTitle>
                        <DialogDescription>Prevent this user from entering your contests.</DialogDescription>
                    </DialogHeader>
                    {/* ... (Dialog content remains same) ... */}
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>Reason</Label>
                            <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="e.g. Bot behavior, spam" />
                        </div>

                        <div className="space-y-2">
                            <Label>Scope</Label>
                            <RadioGroup value={blockScope} onValueChange={(v: any) => setBlockScope(v)}>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="contest" id="scope-contest" />
                                    <Label htmlFor="scope-contest">This Contest Only</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="org" id="scope-org" />
                                    <Label htmlFor="scope-org">Entire Organization</Label>
                                </div>
                            </RadioGroup>
                        </div>

                        <div className="space-y-3 pt-2">
                            <Label>Additional Blocks</Label>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center space-x-2 border p-3 rounded-md">
                                    <Checkbox id="blk-ip" checked={blockIp} onCheckedChange={(c) => setBlockIp(!!c)} />
                                    <Label htmlFor="blk-ip" className="cursor-pointer">Block IP Address</Label>
                                </div>
                                <div className="flex items-center space-x-2 border p-3 rounded-md">
                                    <Checkbox id="blk-dev" checked={blockDevice} onCheckedChange={(c) => setBlockDevice(!!c)} />
                                    <Label htmlFor="blk-dev" className="cursor-pointer">Block Device ID</Label>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground">Use with caution. IP blocks can affect legitimate users.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsBlockModalOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={performBlock} disabled={isBlocking || !blockReason}>
                            {isBlocking ? "Blocking..." : "Block User"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
